import React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {getCheckoutSession} from "../services/subscription-service";
import CheckoutSessionResponse from "../models/CheckoutSessionResponse";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import {useAuth} from "../context/AuthContext";

export default function CheckoutSuccessPage() {
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get('session_id') ?? '';
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<CheckoutSessionResponse | null>(null);

    const {isAuthenticated, refreshSession} = useAuth();

    const amountDisplay = useMemo(() => {
        if (!info?.amount) return null;
        const dollars = (info.amount / 100).toFixed(2);
        return `${info.currency?.toUpperCase() === 'USD' ? '$' : ''}${dollars}`;
    }, [info]);

    useEffect(() => {
        if (!sessionId) {
            setError('Missing checkout session id.');
            setLoading(false);
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await getCheckoutSession(sessionId);
                if (response.status === 200 && response.data) {
                    if(!cancelled) {
                        setInfo(response.data);
                    }
                }
                else {
                    throw new Error('Unable to retrieve checkout session at this time.');
                }
            } catch (e: any) {
                if (!cancelled) setError(e.message || 'Something went wrong.');
            } finally {
                if (!cancelled) setLoading(false);
                if(isAuthenticated) refreshSession();
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [sessionId]);

    return (
        <div>
            <NavBar />
            { isAuthenticated ? (
                <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6" data-testid="auth-container">
                    <div className="w-full max-w-2xl">
                        <div className="bg-white shadow-lg rounded-2xl p-8">
                            {/* Header */}
                            <div className="flex items-center gap-3">
                                <SuccessIcon className="h-8 w-8 text-crumborange" />
                                <h1 className="text-2xl font-semibold text-crumbblue">Subscription activated!</h1>
                            </div>
                            <p className="mt-2 text-gray-600">
                                Your payment was processed successfully. Premium is now active on your account.
                            </p>

                            {/* Loading / Error */}
                            {loading && (
                                <div data-testid="placeholder" className="mt-6 flex items-center gap-3 text-gray-600">
                                    <Spinner />
                                    <span>Confirming your checkout...</span>
                                </div>
                            )}

                            {error && (
                                <div data-testid="error-display" className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
                                    <p className="font-medium">We couldn’t verify the checkout session.</p>
                                    <p data-testid="error-message" className="text-sm mt-1">{error}</p>
                                    <div className="mt-3 text-sm text-red-700/80">
                                        You can head to your dashboard anyway; if Premium doesn’t appear, contact support.
                                    </div>
                                </div>
                            )}

                            {/* Summary Card */}
                            {!loading && !error && (
                                <div data-testid="authed-session-summary" className="mt-6 grid gap-4">
                                    <div className="rounded-xl border border-gray-200 p-4">
                                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-2">
                                            <div>
                                                <dt className="text-xs uppercase tracking-wider text-gray-500">Plan</dt>
                                                <dd data-testid="plan-name" className="text-gray-900 font-medium">{info?.planName ?? 'CrumbCRM Premium'}</dd>
                                            </div>
                                            <div>
                                                <dt className="text-xs uppercase tracking-wider text-gray-500">Status</dt>
                                                <dd data-testid="session-status" className="text-gray-900 font-medium">{info?.status ?? 'Complete'}</dd>
                                            </div>
                                            <div>
                                                <dt className="text-xs uppercase tracking-wider text-gray-500">Email</dt>
                                                <dd data-testid="email" className="text-gray-900">{info?.email ?? '—'}</dd>
                                            </div>
                                            <div>
                                                <dt className="text-xs uppercase tracking-wider text-gray-500">Amount</dt>
                                                <dd data-testid="amount-display" className="text-gray-900">{amountDisplay ?? '—'}</dd>
                                            </div>
                                        </dl>
                                        {info?.receiptUrl && (
                                            <a
                                                href={info.receiptUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="mt-3 inline-block text-sm text-crumbblue hover:underline"
                                            >
                                                View receipt
                                            </a>
                                        )}
                                    </div>

                                    {/* Next steps */}
                                    <div className="rounded-xl bg-gray-50 border border-gray-200 p-4">
                                        <h2 className="text-sm font-semibold text-gray-800">What happens next?</h2>
                                        <ul className="mt-2 list-disc list-inside text-sm text-gray-700 space-y-1">
                                            <li>Go to your dashboard to start using Premium features.</li>
                                            <li>Premium should be active immediately.</li>
                                            <li>If you don’t see Premium right away, refresh and it should sync within a few seconds.</li>
                                        </ul>
                                    </div>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="mt-8 flex">
                                <button
                                    type="button"
                                    data-testid="dashboard-btn"
                                    onClick={() => navigate('/dashboard')}
                                    className="inline-flex justify-center items-center px-5 py-3 rounded-2xl bg-crumborange
                         text-white font-medium shadow hover:opacity-95 focus:outline-none focus:ring-2
                         focus:ring-offset-2 focus:ring-crumborange"
                                >
                                    Go to Dashboard
                                </button>
                            </div>

                            {/* Fallback help */}
                            <p className="mt-4 text-xs text-gray-500">
                                Don’t see Premium immediately? Try refreshing your browser. If it still doesn’t appear, contact support.
                            </p>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6" data-testid="unauthed-summary">
                    <div className="w-full max-w-2xl">
                        <div className="bg-white shadow-lg rounded-2xl p-8">
                            {/* Header */}
                            <div className="flex items-center gap-3">
                                <SuccessIcon className="h-8 w-8 text-crumborange" />
                                <h1 className="text-2xl font-semibold text-crumbblue">Account created!</h1>
                            </div>
                            <p className="mt-2 text-gray-600">
                                Your CrumbCRM account was created and your payment was processed successfully. You can now log in.
                            </p>

                            {/* Loading / Error */}
                            {loading && (
                                <div data-testid="placeholder" className="mt-6 flex items-center gap-3 text-gray-600">
                                    <Spinner />
                                    <span>Confirming your checkout...</span>
                                </div>
                            )}

                            {error && (
                                <div data-testid="error-display" className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
                                    <p className="font-medium">We couldn’t verify the checkout session.</p>
                                    <p data-testid="error-message" className="text-sm mt-1">{error}</p>
                                    <div className="mt-3 text-sm text-red-700/80">
                                        Please try logging in anyway or contact support if you run into issues.
                                    </div>
                                </div>
                            )}

                            {/* Summary Card */}
                            {!loading && !error && (
                                <div data-testid="unauthed-session-summary" className="mt-6 grid gap-4">
                                    <div className="rounded-xl border border-gray-200 p-4">
                                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-2">
                                            <div>
                                                <dt className="text-xs uppercase tracking-wider text-gray-500">Plan</dt>
                                                <dd data-testid="plan-name" className="text-gray-900 font-medium">{info?.planName ?? 'CrumbCRM Premium'}</dd>
                                            </div>
                                            <div>
                                                <dt className="text-xs uppercase tracking-wider text-gray-500">Status</dt>
                                                <dd data-testid="session-status" className="text-gray-900 font-medium">{info?.status ?? 'Complete'}</dd>
                                            </div>
                                            <div>
                                                <dt className="text-xs uppercase tracking-wider text-gray-500">Email</dt>
                                                <dd data-testid="email" className="text-gray-900">{info?.email ?? '—'}</dd>
                                            </div>
                                            <div>
                                                <dt className="text-xs uppercase tracking-wider text-gray-500">Amount</dt>
                                                <dd data-testid="amount-display" className="text-gray-900">{amountDisplay ?? '—'}</dd>
                                            </div>
                                        </dl>
                                        {info?.receiptUrl && (
                                            <a
                                                href={info.receiptUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="mt-3 inline-block text-sm text-crumbblue hover:underline"
                                            >
                                                View receipt
                                            </a>
                                        )}
                                    </div>

                                    {/* Next steps */}
                                    <div className="rounded-xl bg-gray-50 border border-gray-200 p-4">
                                        <h2 className="text-sm font-semibold text-gray-800">What happens next?</h2>
                                        <ul className="mt-2 list-disc list-inside text-sm text-gray-700 space-y-1">
                                            <li>Use the button below to log in to your new account.</li>
                                            <li>Your premium features will be active immediately after login.</li>
                                            <li>If you don’t see the premium plan right away, refresh and it should sync within a few seconds.</li>
                                        </ul>
                                    </div>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                                <button
                                    type="button"
                                    data-testid="login-btn"
                                    onClick={() => navigate('/login')}
                                    className="inline-flex justify-center items-center px-5 py-3 rounded-2xl bg-crumborange
                                    text-white font-medium shadow hover:opacity-95 focus:outline-none focus:ring-2
                                    focus:ring-offset-2 focus:ring-crumborange"
                                >
                                    Go to Login
                                </button>
                                <button
                                    type="button"
                                    data-testid="home-btn"
                                    onClick={() => navigate('/')}
                                    className="inline-flex justify-center items-center px-5 py-3 rounded-2xl bg-white
                                    text-crumbblue font-medium shadow border border-gray-200 hover:bg-gray-50
                                    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-crumbblue"
                                >
                                    Back to Home
                                </button>
                            </div>

                            {/* Fallback help */}
                            <p className="mt-4 text-xs text-gray-500">
                                Trouble logging in after checkout? Clear your cache or try a private window. If problems persist, contact support.
                            </p>
                        </div>
                    </div>
                </div>
            )}
            <Footer />
        </div>
    );
}

function SuccessIcon({ className = '' }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10Z" fill="currentColor" opacity="0.12" />
            <path d="M16.5 9.5l-5 5-2-2" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function Spinner() {
    return (
        <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
        </svg>
    );
}
